import React, { memo, useCallback } from 'react';
import { Link } from '@reach/router';
import { useTranslation } from 'react-i18next';

import { GiftCardItem } from 'olo-service';

import { DetailedCartItem, useCartDispatch } from 'hooks/context/cart';
import { useGiftCardCartDispatch } from 'hooks/context/giftCardCart';
import { useGetUnavailableItems } from 'hooks/useGetUnavailableItems';

import styles from './LineItem.module.scss';
import { InlineCommaList } from 'components/InlineCommaList/InlineCommaList';
import { LineItemLayout } from '../LineItemLayout';
import { LINE_ITEM_TEST_ID } from '../LineItems.const';

const LineItemContent: React.FC<{ item: DetailedCartItem; onRemove?: React.MouseEventHandler }> = memo(function LineItemContent({ item, onRemove }) {
  const { t } = useTranslation();
  const { unavailableItems } = useGetUnavailableItems();

  const { id: itemId, name: itemName, quantity, total, modifiers, note, discounts = [] } = item;

  const calculatedDiscounts = discounts.map((discount) => {
    if (discount.percentage) {
      return {
        name: discount.name,
        amount: discount.percentage * total / quantity / 100
      };
    }

    return {
      name: discount.name,
      amount: discount.amount ? discount.amount / 100 : undefined
    };
  });

  return (
    <LineItemLayout
      name={t('cart.item.name', { itemName, count: quantity })}
      price={total}
      discounts={calculatedDiscounts}
      onRemove={onRemove}
      available={!unavailableItems.includes(itemId)}
    >
       {!note && !modifiers.length ? null :
       <InlineCommaList data-testid={LINE_ITEM_TEST_ID.modifier}>
        {modifiers.map((modifier, index) => (
          <li key={index}>{modifier.name}</li>
        ))}
        {note && <li data-testid={LINE_ITEM_TEST_ID.note}>"{note}"</li>}
      </InlineCommaList>}
    </LineItemLayout>
  );
});

export const LineItem: React.FC<{ item: DetailedCartItem; onClick?: () => void, readOnly?: boolean; }> = memo(function LineItem({ item, onClick, readOnly }) {
  const { t } = useTranslation();
  const { remove } = useCartDispatch();

  const { cartId } = item;

  const onRemoveItemClick: React.MouseEventHandler = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    remove(cartId);
  }, [cartId, remove]);

  if (readOnly) {
    return (
      <div className={styles.lineItem} data-testid={LINE_ITEM_TEST_ID.root}>
        <LineItemContent item={item} />
      </div>
    );
  }

  return (
    <Link to={`item/${cartId}`} className={styles.lineItem} title={t('cart.item.edit')} onClick={onClick} data-testid={LINE_ITEM_TEST_ID.root}>
      <LineItemContent item={item} onRemove={onRemoveItemClick} />
    </Link>
  );
});

const GiftCardLineItemContent: React.FC<{ item: GiftCardItem; onRemove?: React.MouseEventHandler }> = memo(function GiftCardLineItemContent({ item, children, onRemove }) {
  const { t } = useTranslation();

  const { amount, note, recipient: { isCustomer, name, email } } = item;

  return (
    <LineItemLayout
      name={t('giftCards.digitalGiftCard')}
      price={amount}
      onRemove={onRemove}
    >
      <ul data-testid={LINE_ITEM_TEST_ID.modifier}>
        {!isCustomer && <>
          <li>{name}</li>
          <li>{email}</li>
        </>}
        {!!note && <li>"{note}"</li>}
      </ul>
    </LineItemLayout>
  );
});

export const GiftCardLineItem: React.FC<{ index: number; item: GiftCardItem; onClick?: () => void, readOnly?: boolean }> = memo(function GiftCardLineItem({ index, item, onClick, readOnly }) {
  const { t } = useTranslation();
  const { remove } = useGiftCardCartDispatch();

  const onRemoveItemClick: React.MouseEventHandler = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    remove(index);
  }, [remove, index]);

  if (readOnly) {
    return (
      <div className={styles.lineItem} data-testid={LINE_ITEM_TEST_ID.root}>
        <GiftCardLineItemContent item={item} />
      </div>
    );
  }

  return (
    <Link to={`gift-card/${index}`} className={styles.lineItem} title={t('cart.item.edit')} onClick={onClick} data-testid={LINE_ITEM_TEST_ID.root}>
      <GiftCardLineItemContent item={item} onRemove={onRemoveItemClick} />
    </Link>
  );
});
